import { Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { IContentLocation, IMouseEngineEvent, IReaderViewAnnotation } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { BehaviorSubject } from "rxjs";
import { textNotesFonts } from "src/app/core/common";
import { EpubStrokeColor } from "src/app/core/epub-utils/enums";
import { ITextNoteFont } from "src/app/core/interfaces";
import { DocumentManagerService } from "../document/document-manager.service";
import { BaseEpubAnnotationService } from "./base-epub-annotation.service";
import { ILocator } from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import { CommandInvokerService } from "../command/command-invoker.service";

export interface IEpubBookmarkData {
    bookmarkId: number;
    text: string;
    color: string;
    fontColor: EpubStrokeColor;
    fontSize: number;
    font: ITextNoteFont;
    textSizeX: number;
    textSizeY: number;
}

@Injectable({
    providedIn: 'root'
})
export class EpubBookmarkService extends BaseEpubAnnotationService<IEpubBookmarkData> {
    public readonly LAYER_NAME = 'bookmarks';
    public focusedBookmark$ = new BehaviorSubject<IEpubBookmarkData | undefined>(undefined);
    public hoveredBookmark$ = new BehaviorSubject<IEpubBookmarkData | undefined>(undefined);

    private renderer: Renderer2;

    public readonly defaultColor = '#575756';
    public readonly defaultFontColor = EpubStrokeColor.Black;
    public readonly defaultFontSize = 12;
    public readonly defaultFont = textNotesFonts[0];
    public readonly defaultTextSizeX = 200;
    public readonly defaultTextSizeY = 200;

    constructor(rendererFactory: RendererFactory2, documentManagerService: DocumentManagerService, commandInvoker: CommandInvokerService) {
        super(documentManagerService, commandInvoker);
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    protected createAnnotation(locator: ILocator, data: IEpubBookmarkData) {
        this.createBookmarkAnnotation(locator, data);
    }

    public getBookmarks(): IReaderViewAnnotation<IEpubBookmarkData>[] {
        if (!this.annotationLayer) return [];
        return this.annotationLayer.getAnnotations().filter((data) => data.getCustomData() != null);;
    }

    public createBookmark(location: IContentLocation) {
        const bookmarks = this.annotationLayer?.getAnnotations();
        if (!bookmarks || !this.docId) return;

        const bookmarkId = bookmarks.length + 1;
        const data = {
            bookmarkId: bookmarkId,
            text: '',
            color: this.defaultColor,
            fontColor: this.defaultFontColor,
            fontSize: this.defaultFontSize,
            font: this.defaultFont,
            textSizeX: this.defaultTextSizeX,
            textSizeY: this.defaultTextSizeY
        } as IEpubBookmarkData;

        this.createBookmarkAnnotation(location, data, true);
    }

    public createOrOpenBookmark(clickEvent: IMouseEngineEvent) {
        if (!this.isValidEvent(clickEvent) || !this.docId) return;

        const location = clickEvent.target!.contentLocation!;
        const existingBookmark = this.getBookmarkByLocation(clickEvent.target!.contentLocation!);

        if (existingBookmark) {
            this.showBookmarkEditor(existingBookmark);
            return;
        }

        this.createBookmark(location);
    }

    public updateFocusedBookmarkData(data: Partial<IEpubBookmarkData>) {
        const focusedBookmarkId = this.focusedBookmark$.value?.bookmarkId;
        if (!focusedBookmarkId) return;

        const bookmark = this.annotationLayer?.getAnnotations();
        if (!bookmark) return;

        const bookmarkToUpdate = bookmark.find(bookmark => bookmark.getCustomData()?.bookmarkId === focusedBookmarkId);
        if (bookmarkToUpdate && bookmarkToUpdate.getCustomData()) {
            bookmarkToUpdate.setCustomData({ ...bookmarkToUpdate.getCustomData()!, ...data });
            this.emitDocChanged(this.docId!);
        }
    }

    public updateFocusedBookmarkColor(color: string) {
        if (!this.focusedBookmark$.value) return;
        this.updateFocusedBookmarkData({ color: color });
        const bookmarkElement = this.getBookmarkHtmlElement(this.focusedBookmark$.value);
        if (bookmarkElement) {
            this.renderer.setStyle(bookmarkElement, 'color', color);
            this.emitDocChanged(this.docId!);
        }
    }

    public deleteFocusedBookmark() {
        const focusedBookmarkId = this.focusedBookmark$.value?.bookmarkId;
        if (!focusedBookmarkId) return;

        this.deleteBookmarkById(focusedBookmarkId);
    }

    public deleteBookmarkById(id: number) {
        const bookmarks = this.annotationLayer?.getAnnotations();
        if (!bookmarks) return;

        const bookmarkToDelete = bookmarks.find(bookmark => bookmark.getCustomData()?.bookmarkId === id);
        if (!bookmarkToDelete) return;

        this.annotationLayer?.destroyAnnotation(bookmarkToDelete);
        this.focusedBookmark$.next(undefined);
        this.emitDocChanged(this.docId!);
    }

    public getBookmarkHtmlElement(data: IEpubBookmarkData): HTMLElement | null {
        if (!this.docId) return null;

        return document.querySelector(`.${this.getBookmarkClassName(data.bookmarkId, this.docId)}`) as HTMLElement;
    }

    public attachBookmarkIcon(bookmark: IReaderViewAnnotation<IEpubBookmarkData>): void {
        const bookmarkData = bookmark.getCustomData();
        if (!bookmarkData) return;
        const container = this.getBookmarkHtmlElement(bookmarkData);
        if (container && !container.querySelector('.material-icons')) {
            const icon = this.renderer.createElement('i');
            this.renderer.addClass(icon, 'material-icons');
            const text = this.renderer.createText('bookmark');
            this.renderer.appendChild(icon, text);
            this.renderer.appendChild(container, icon);
            this.renderer.setStyle(container, 'color', bookmarkData.color);

            const bookmarks = this.annotationLayer?.getAnnotations();
            if (!bookmarks) return undefined;

            container.addEventListener('mouseenter', () => {
                const hoveredBookmarkData = bookmarks.find(n => n.getCustomData()?.bookmarkId === bookmarkData.bookmarkId)?.getCustomData();
                if (hoveredBookmarkData) this.hoveredBookmark$.next(hoveredBookmarkData);
            });
            container.addEventListener('mouseleave', () => {
                this.hoveredBookmark$.next(undefined);
            });
        }
    }

    public showBookmarkEditor(bookmark: IReaderViewAnnotation<IEpubBookmarkData>) {
        if (this.isBookmarkAnnotation(bookmark)) {
            const bookmarkData = bookmark.getCustomData()!;
            this.focusedBookmark$.next(bookmarkData);
        }
    }

    private async createBookmarkAnnotation(location: IContentLocation | ILocator, data: IEpubBookmarkData, showBookmarkEditor = false) {
        const bookmarkClassName = this.getBookmarkClassName(data.bookmarkId, this.docId!);
        const bookmark = this.annotationLayer!.createAnnotation<IEpubBookmarkData>(await this.getBookmarkLocationEnd(location));
        bookmark.setOptions({ positionClassName: bookmarkClassName });
        bookmark.setCustomData(data);

        setTimeout(() => {
            this.attachBookmarkIcon(bookmark);
            if (showBookmarkEditor) {
                this.showBookmarkEditor(bookmark);
            }
        }, 0);
        this.emitDocChanged(this.docId!);
    }

    private async getBookmarkLocationEnd(location: IContentLocation | ILocator): Promise<IContentLocation> {
        if (typeof (location as any).collapseToEnd === 'function') return (location as IContentLocation).collapseToEnd();

        return (await this.readerPublication!.fetchContentLocation(location as ILocator)).collapseToEnd();
    }

    private isValidEvent(event: IMouseEngineEvent): boolean {
        return !!(event?.target?.contentLocation && this.annotationLayer);
    }

    private getBookmarkClassName(bookmarkNumber: number, docId: string) {
        return `epub-bookmark-${bookmarkNumber}-${docId}`;
    }

    private isBookmarkAnnotation(annotation: IReaderViewAnnotation<any>): annotation is IReaderViewAnnotation<IEpubBookmarkData> {
        return !!(annotation && annotation.getCustomData() && typeof (annotation.getCustomData() as IEpubBookmarkData).bookmarkId === 'number');
    }

    private getBookmarkByLocation(location: IContentLocation): IReaderViewAnnotation<IEpubBookmarkData> | undefined {
        const bookmarks = this.annotationLayer?.getAnnotations();
        if (!bookmarks) return undefined;

        return bookmarks.find(async bookmark => (await this.readerPublication!.fetchContentLocation(bookmark.getLocator()!)).collapseToEnd().equals(location.collapseToEnd()))
    }
}